/**
 * User Detail Modal - CRM-style user profile view
 * Shows contact details on left, activity timeline on right
 */
import React from 'react';
import { 
  X, Mail, Calendar, Briefcase, CreditCard, FolderOpen, 
  UserPlus, LogIn, Sparkles, Palette, MinusCircle, Clock,
  User, Building, Activity, ChevronRight, Linkedin, MapPin, ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';

// Icon mapping for timeline events
const TimelineIcon = ({ type }) => {
  const iconMap = {
    'registration': <UserPlus className="w-4 h-4 text-green-600" />,
    'login': <LogIn className="w-4 h-4 text-blue-500" />,
    'project_created': <FolderOpen className="w-4 h-4 text-indigo-600" />,
    'design_generated': <Sparkles className="w-4 h-4 text-amber-500" />,
    'theme_request': <Palette className="w-4 h-4 text-purple-600" />,
    'credit_transaction': <CreditCard className="w-4 h-4 text-emerald-600" />,
    'credit_used': <MinusCircle className="w-4 h-4 text-red-500" />
  };
  return iconMap[type] || <Activity className="w-4 h-4 text-zinc-400" />;
};

// Format timestamp to readable string
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

export const UserDetailModal = ({ isOpen, onClose, userData, loading }) => {
  if (!isOpen) return null;
  
  const { user, workspaces, projects, timeline, stats } = userData || {};
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-white">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-12 h-12 rounded-full border-2 border-white shadow"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="w-6 h-6 text-indigo-600" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-zinc-900">{user?.name || 'Unknown User'}</h2>
              <p className="text-sm text-zinc-500">{user?.email}</p>
            </div>
            <span className={`ml-4 px-3 py-1 rounded-full text-xs font-medium ${
              user?.role === 'Admin' ? 'bg-purple-100 text-purple-800' :
              user?.role === 'Customer' ? 'bg-blue-100 text-blue-800' :
              'bg-zinc-100 text-zinc-700'
            }`}>
              {user?.role}
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Side - Contact Details */}
            <div className="w-1/3 border-r bg-zinc-50/50 overflow-y-auto p-6 space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-white">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{stats?.total_projects || 0}</div>
                    <div className="text-xs text-zinc-500 mt-1">Projects</div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-amber-500">{stats?.total_designs || 0}</div>
                    <div className="text-xs text-zinc-500 mt-1">Designs</div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{stats?.total_logins || 0}</div>
                    <div className="text-xs text-zinc-500 mt-1">Logins</div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {workspaces?.reduce((sum, w) => sum + (w.credits || 0), 0) || 0}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">Credits</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Contact Information */}
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-600" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-zinc-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-zinc-500">Email</div>
                        <div className="text-sm font-medium text-zinc-900">{user?.email}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-zinc-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-zinc-500">Registered</div>
                        <div className="text-sm font-medium text-zinc-900">{formatDate(user?.registered_at)}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-zinc-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-zinc-500">Last Active</div>
                        <div className="text-sm font-medium text-zinc-900">{formatTimestamp(stats?.last_active)}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* LinkedIn/ContactOut Profile */}
              {user?.linkedin && !user.linkedin.not_found && (
                <Card className="bg-white border-blue-100">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                      <Linkedin className="w-4 h-4 text-blue-600" />
                      Professional Profile
                      <span className="text-xs font-normal text-zinc-400 ml-auto">via ContactOut</span>
                    </h3>
                    
                    {/* Profile Picture and Headline */}
                    {(user.linkedin.profile_picture || user.linkedin.headline) && (
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                        {user.linkedin.profile_picture && (
                          <img 
                            src={user.linkedin.profile_picture} 
                            alt="" 
                            className="w-12 h-12 rounded-full object-cover border"
                          />
                        )}
                        <div>
                          {user.linkedin.full_name && (
                            <div className="font-medium text-zinc-900">{user.linkedin.full_name}</div>
                          )}
                          {user.linkedin.headline && (
                            <div className="text-sm text-zinc-600">{user.linkedin.headline}</div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      {/* Current Position */}
                      {user.linkedin.job_title && (
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Job Title</div>
                            <div className="text-sm font-medium text-zinc-900">{user.linkedin.job_title}</div>
                          </div>
                        </div>
                      )}
                      
                      {user.linkedin.company && (
                        <div className="flex items-start gap-3">
                          <Building className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Company</div>
                            <div className="text-sm font-medium text-zinc-900">
                              {user.linkedin.company}
                              {user.linkedin.company_website && (
                                <a 
                                  href={user.linkedin.company_website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-2 text-blue-500 hover:text-blue-700"
                                >
                                  <ExternalLink className="w-3 h-3 inline" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {user.linkedin.industry && (
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Industry</div>
                            <div className="text-sm font-medium text-zinc-900">{user.linkedin.industry}</div>
                          </div>
                        </div>
                      )}
                      
                      {user.linkedin.seniority && (
                        <div className="flex items-start gap-3">
                          <User className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Seniority</div>
                            <div className="text-sm font-medium text-zinc-900">{user.linkedin.seniority}</div>
                          </div>
                        </div>
                      )}
                      
                      {user.linkedin.department && (
                        <div className="flex items-start gap-3">
                          <Building className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Department</div>
                            <div className="text-sm font-medium text-zinc-900">{user.linkedin.department}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Location */}
                      {(user.linkedin.location || user.linkedin.city) && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-zinc-400 mt-0.5" />
                          <div>
                            <div className="text-xs text-zinc-500">Location</div>
                            <div className="text-sm font-medium text-zinc-900">
                              {user.linkedin.location || [user.linkedin.city, user.linkedin.state, user.linkedin.country].filter(Boolean).join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Summary */}
                      {user.linkedin.summary && (
                        <div className="pt-2 border-t mt-3">
                          <div className="text-xs text-zinc-500 mb-1">Summary</div>
                          <div className="text-sm text-zinc-700 line-clamp-3">{user.linkedin.summary}</div>
                        </div>
                      )}
                      
                      {/* Skills */}
                      {user.linkedin.skills && user.linkedin.skills.length > 0 && (
                        <div className="pt-2 border-t mt-3">
                          <div className="text-xs text-zinc-500 mb-2">Skills</div>
                          <div className="flex flex-wrap gap-1">
                            {user.linkedin.skills.slice(0, 10).map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 bg-zinc-100 text-zinc-700 text-xs rounded-full">
                                {typeof skill === 'string' ? skill : skill.name || skill}
                              </span>
                            ))}
                            {user.linkedin.skills.length > 10 && (
                              <span className="px-2 py-0.5 text-zinc-500 text-xs">
                                +{user.linkedin.skills.length - 10} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Contact Info */}
                      {(user.linkedin.personal_emails?.length > 0 || user.linkedin.phone_numbers?.length > 0) && (
                        <div className="pt-2 border-t mt-3">
                          <div className="text-xs text-zinc-500 mb-2">Additional Contact</div>
                          {user.linkedin.personal_emails?.map((email, i) => (
                            <div key={i} className="text-sm text-zinc-700 flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {email}
                            </div>
                          ))}
                          {user.linkedin.phone_numbers?.map((phone, i) => (
                            <div key={i} className="text-sm text-zinc-700 flex items-center gap-2">
                              📞 {phone}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Social Links */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t mt-3">
                        {user.linkedin.linkedin_url && (
                          <a 
                            href={user.linkedin.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-700 text-xs font-medium transition-colors"
                          >
                            <Linkedin className="w-3.5 h-3.5" />
                            LinkedIn
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {user.linkedin.twitter_url && (
                          <a 
                            href={user.linkedin.twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 hover:bg-sky-100 rounded-lg text-sky-700 text-xs font-medium transition-colors"
                          >
                            𝕏 Twitter
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {user.linkedin.github_url && (
                          <a 
                            href={user.linkedin.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-zinc-700 text-xs font-medium transition-colors"
                          >
                            GitHub
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {user.linkedin.facebook_url && (
                          <a 
                            href={user.linkedin.facebook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-800 text-xs font-medium transition-colors"
                          >
                            Facebook
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Workspaces */}
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Building className="w-4 h-4 text-indigo-600" />
                    Workspaces ({workspaces?.length || 0})
                  </h3>
                  {workspaces && workspaces.length > 0 ? (
                    <div className="space-y-2">
                      {workspaces.map((ws, i) => (
                        <div key={i} className="p-3 bg-zinc-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-zinc-900">{ws.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              ws.role === 'owner' ? 'bg-indigo-100 text-indigo-700' : 'bg-zinc-200 text-zinc-600'
                            }`}>
                              {ws.role}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                            <span className="font-mono bg-white px-1.5 py-0.5 rounded">{ws.display_id}</span>
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              {ws.credits} credits
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-400 text-center py-2">No workspaces</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Projects */}
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-indigo-600" />
                    Projects ({projects?.length || 0})
                  </h3>
                  {projects && projects.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {projects.map((p, i) => (
                        <div key={i} className="p-3 bg-zinc-50 rounded-lg flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm text-zinc-900">
                              {p.business_info?.business_name || 'Unnamed Project'}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono">{p.display_id || p.project_id}</div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            p.status === 'completed' ? 'bg-green-100 text-green-700' :
                            p.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            p.status === 'draft' ? 'bg-zinc-100 text-zinc-600' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {p.status || 'draft'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-400 text-center py-2">No projects</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Right Side - Activity Timeline */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="font-semibold text-zinc-900 mb-6 flex items-center gap-2 sticky top-0 bg-white pb-4 border-b">
                <Activity className="w-5 h-5 text-indigo-600" />
                Activity Timeline
                <span className="ml-2 text-sm font-normal text-zinc-500">
                  ({timeline?.length || 0} events)
                </span>
              </h3>
              
              {timeline && timeline.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-zinc-200" />
                  
                  {/* Timeline events */}
                  <div className="space-y-4">
                    {timeline.map((event, i) => (
                      <div key={i} className="relative flex gap-4 pl-10">
                        {/* Timeline dot */}
                        <div className="absolute left-2 w-5 h-5 rounded-full bg-white border-2 border-zinc-200 flex items-center justify-center">
                          <TimelineIcon type={event.type} />
                        </div>
                        
                        {/* Event content */}
                        <div className="flex-1 bg-zinc-50 rounded-lg p-4 hover:bg-zinc-100 transition-colors">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-zinc-900">{event.title}</div>
                              <div className="text-sm text-zinc-600 mt-0.5">{event.description}</div>
                            </div>
                            <div className="text-xs text-zinc-400 whitespace-nowrap ml-4">
                              {formatTimestamp(event.timestamp)}
                            </div>
                          </div>
                          {(event.project_id || event.design_id) && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                              {event.display_id && (
                                <span className="font-mono bg-white px-1.5 py-0.5 rounded">{event.display_id}</span>
                              )}
                              {event.project_id && !event.display_id && (
                                <span className="font-mono bg-white px-1.5 py-0.5 rounded">{event.project_id.slice(0, 15)}...</span>
                              )}
                              <ChevronRight className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-400">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No activity recorded yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailModal;
